import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import { Task } from "@/types";
import Group from "@/components/atoms/Group";
import Stack from "@/components/atoms/Stack";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import Alert from "@/components/atoms/Alert";
import Loader from "@/components/atoms/Loader";
import { useAuth } from "@/contexts/AuthContext";
import UserRoleTypeEnum from "@/constants/enums/UserRoleTypeEnum";
import { useDecideDeviations, useGetOrderInspection } from "@/services/Inspections";
import { getApiError } from "@/utils/apiErrors";
import { SuccessNotificationObj } from "@/utils/NotificationUtil";
import { ReceiptAccordion, WeightAccordion } from "@/components/molecules/DrawerOrders/TabsContent/InspectionTab";
import DeviationProductsAccordion from "@/components/molecules/DrawerOrders/TabsContent/InspectionTab/DeviationProductsAccordion";
import DeviationServicesAccordion from "@/components/molecules/DrawerOrders/TabsContent/InspectionTab/DeviationServicesAccordion";
import DeviationCostsAccordion from "@/components/molecules/DrawerOrders/TabsContent/InspectionTab/DeviationCostsAccordion";
import { Row } from "@/components/molecules/DrawerOrders/TabsContent/InspectionTab/TabParts";
import DeviationProductDecisions from "./DeviationProductDecisions";
import DeviationServiceDecisions from "./DeviationServiceDecisions";
import { isPendingDeviation, useDeviationDecisionsForm } from "./useDeviationDecisionsForm";
import { useDecisionPreview, withPreview } from "./useDecisionPreview";
import styles from "../styles.module.css";

interface DeviationConfirmationTaskProps {
  task: Task;
  onClose: () => void;
}

interface SaveError {
  message: string;
  /** Someone may have decided meanwhile; the inspection is reloaded either way. */
  mayBeStale: boolean;
}

/**
 * The deviations an inspection held for confirmation, on the buyer's order. Deviation managers and admins confirm
 * (optionally correcting the weight or number) or reject them, any number at a time, in one save; the last decision
 * finalizes the order and completes the task. Everyone else sees them read-only.
 */
export default function DeviationConfirmationTask({ task, onClose }: DeviationConfirmationTaskProps) {
  const { t } = useTranslation();
  const { currentCompany, hasRole, isAdminUser } = useAuth();
  const canDecide = isAdminUser(currentCompany) || hasRole(currentCompany, UserRoleTypeEnum.DEVIATION_MANAGER);

  // The task is connected to the buyer's order. Newest inspection first; only the original order's has deviations.
  const { data, isLoading, isError } = useGetOrderInspection(task.connected, { retry: false });
  const inspection = data?.inspections[0];
  const form = useDeviationDecisionsForm(inspection);
  const preview = useDecisionPreview(task.connected, form, canDecide);
  const [saveError, setSaveError] = useState<SaveError | null>(null);

  const { mutate: decide, isPending: isSaving } = useDecideDeviations({
    onSuccess: (result, { orderId }) => {
      const isDone = !result.inspections[0]?.deviations.some(isPendingDeviation);
      form.reset();
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          isDone ? t("inspections:decisions.completed", { orderId }) : t("inspections:decisions.saved", { orderId }),
          "deviation-decisions-success"
        )
      );
      // The order is finalized and the task completed, so there's nothing left to do here.
      if (isDone) onClose();
    },
    onError: (error) => {
      const { status, errorCode, reason } = getApiError(error);
      if (errorCode === 182 || status === 409) {
        setSaveError({ message: t("inspections:decisions.errors.notPending"), mayBeStale: true });
      } else if (errorCode === 183) {
        const message = t("inspections:decisions.errors.invalid", {
          reason: reason ?? t("inspections:errors.noReason"),
        });
        setSaveError({ message, mayBeStale: true });
      } else if (status === 403) {
        setSaveError({ message: t("inspections:decisions.readOnly"), mayBeStale: false });
      } else {
        setSaveError({ message: t("inspections:decisions.errors.failed"), mayBeStale: false });
      }
    },
  });

  const save = () => {
    if (!data) return;
    setSaveError(null);
    decide({ orderId: data.orderId, decisions: form.decisions });
  };

  const isEditable = canDecide && form.pending.length > 0;
  const products = inspection?.deviations.filter((deviation) => deviation.kind === "PRODUCT") ?? [];
  const services = inspection?.deviations.filter((deviation) => deviation.kind === "SERVICE") ?? [];

  return (
    <Stack className="full flex col between h-100 py-10" gap={0}>
      <Group gap="20px" align="center" className={styles.taskHeader}>
        <SVGAsset name="purchaseIndicator" />
        <Text size="md" display="technical" tt="uppercase" strong={true}>
          {t("enumerables:taskTypes.DEVIATION_CONFIRMATION")}
        </Text>
      </Group>

      <Stack className={styles.scrollPanel} gap={0} data-testid="deviation-confirmation-task">
        <Grid p={20}>
          <Row label={t("common:order")}>{`#${task.connected}`}</Row>
          {task.counterParty && <Row label={t("tasks:counterParty")}>{task.counterParty}</Row>}
          {task.dueDate && <Row label={t("tasks:dueDate")}>{dayjs(task.dueDate).format("DD.MM.YYYY")}</Row>}
        </Grid>

        {isLoading ? (
          <Loader />
        ) : isError || !data || !inspection ? (
          <Alert color="error" m={20} data-testid="deviation-confirmation-load-error">
            {t("inspections:decisions.loadFailed")}
          </Alert>
        ) : (
          <>
            {!canDecide && (
              <Alert m={20} data-testid="deviation-confirmation-read-only">
                {t("inspections:decisions.readOnly")}
              </Alert>
            )}
            {canDecide && form.pending.length === 0 && (
              <Alert color="success" m={20} data-testid="deviation-confirmation-done">
                {t("inspections:decisions.nothingPending")}
              </Alert>
            )}
            <WeightAccordion inspection={inspection} prefillWeightKg={data.prefillWeightKg} />
            <ReceiptAccordion inspection={inspection} isBuyer />
            {products.length > 0 &&
              (canDecide ? (
                <DeviationProductDecisions deviations={products} form={form} preview={preview} />
              ) : (
                <DeviationProductsAccordion deviations={products} isBuyer />
              ))}
            {services.length > 0 &&
              (canDecide ? (
                <DeviationServiceDecisions deviations={services} form={form} preview={preview} />
              ) : (
                <DeviationServicesAccordion deviations={services} isBuyer />
              ))}
            {/* With the unsaved decisions applied while the manager decides, as saved otherwise. */}
            {inspection.deviations.length > 0 && (
              <DeviationCostsAccordion inspection={withPreview(inspection, preview.preview)} />
            )}
            {preview.error && (
              <Text size="xs" color="error" px={20} pb={10} data-testid="deviation-decision-preview-error">
                {preview.error}
              </Text>
            )}
            {saveError && (
              <Alert color="error" m={20} data-testid="deviation-confirmation-save-error">
                <Text size="sm">{saveError.message}</Text>
                {saveError.mayBeStale && <Text size="sm">{t("inspections:decisions.errors.reloaded")}</Text>}
              </Alert>
            )}
          </>
        )}
      </Stack>

      <Group className={styles.tabActionGroup} p={15} gap={16} align="center" justify="space-between">
        {/* Keeps the buttons on the right when there's no "confirm all". */}
        <div>
          {isEditable && (
            <CTA
              intent="tertiary"
              onClick={form.confirmAll}
              disabled={!form.hasUndecided}
              data-testid="deviation-confirm-all"
            >
              {t("inspections:decisions.confirmAll")}
            </CTA>
          )}
        </div>
        <Group gap={16}>
          <CTA intent="secondary" onClick={onClose}>
            {isEditable ? t("actions:cancel") : t("actions:close")}
          </CTA>
          {isEditable && (
            <CTA
              onClick={save}
              disabled={form.decisions.length === 0 || Object.keys(form.errors).length > 0}
              loading={isSaving}
              data-testid="deviation-decisions-save"
            >
              {t("actions:save")}
            </CTA>
          )}
        </Group>
      </Group>
    </Stack>
  );
}
